// Preferences (route /more/preferences) — language, text size, haptics, Apple
// Health and reminders.
//
// Lifted out of More.tsx, which had grown to a single 563-line scroll. iOS
// Settings works because the landing page is a short index and the controls
// live one level down; this is that. Every piece of state here is local to
// these controls, so nothing had to be threaded back to the hub.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Bell, Type, Vibrate, HeartPulse, Check, Moon, Sun } from "lucide-react";
import { api } from "../lib/api";
import { useT, useLocale, LOCALE_LABEL, apiLang } from "../lib/i18n";
import { todayLocal } from "../lib/date";
import { savedTheme, setTheme, type Theme } from "../lib/theme";
import { TEXT_ZOOMS, getTextZoom, setTextZoom, type TextZoom } from "../lib/textSize";
import { hapticSelect, hapticsDisabled, setHapticsDisabled } from "../lib/haptics";
import { useConfirm } from "../lib/confirm";
import { useToast } from "../lib/toast";
import { SettingsGroup, SettingsButton, IconChip, Toggle } from "../components/ui/SettingsList";
import { PageHeader } from "../components/ui/PageHeader";
import { LanguageSheet } from "../components/LanguageSheet";
import { pushSupported, isIos, isStandalone } from "../lib/push";
import { isNativeApp } from "../lib/native";
import { healthSupported, healthConnected, connectHealth, syncHealthSteps, disconnectHealth } from "../lib/health";

const TINT = { language: "#3b82f6", reminders: "#f59e0b", health: "#ef4444" };

export function MorePreferences() {
  const t = useT();
  const { locale } = useLocale();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();

  const [showLang, setShowLang] = useState(false);
  const [theme, setThemeState] = useState<Theme>(() => savedTheme());
  const [textZoom, setTextZoomState] = useState<TextZoom>(() => getTextZoom());
  const [hapticsOn, setHapticsOn] = useState(() => !hapticsDisabled());
  const [enabledCount, setEnabledCount] = useState<number | null>(null);
  const [healthAvailable, setHealthAvailable] = useState(false);
  const [healthOn, setHealthOn] = useState(healthConnected());
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthToday, setHealthToday] = useState<{ steps: number | null; active_kcal: number | null } | null>(null);

  const remindersAvailable = isNativeApp() || (pushSupported() && !(isIos() && !isStandalone()));
  const enabledSummary =
    enabledCount === null ? "" : enabledCount === 0 ? t("reminders.none_on") : t("reminders.n_on", { n: String(enabledCount) });

  useEffect(() => {
    // Just enough to label the row; the reminders screen owns the detail.
    api.getReminders()
      .then((r) => setEnabledCount(r.master ? r.settings.filter((s) => s.enabled).length : 0))
      .catch(() => { /* leave the row unlabelled rather than guessing */ });
    healthSupported().then(setHealthAvailable).catch(() => setHealthAvailable(false));
  }, []);

  // What Health actually contributed today — the point of the connection, so
  // it belongs beside the connection rather than on the logging screen.
  useEffect(() => {
    if (!healthOn) { setHealthToday(null); return; }
    api.today(todayLocal(), apiLang(locale))
      .then((r) => setHealthToday({ steps: r.steps, active_kcal: r.active_kcal }))
      .catch(() => setHealthToday(null));
  }, [healthOn, locale]);

  async function tapHealth() {
    setHealthBusy(true);
    try {
      if (!healthOn) {
        await connectHealth();
        setHealthOn(true);
        toast.success(t("health.connected_toast"));
      } else {
        const disconnect = !(await confirm({
          message: t("health.connected_prompt"),
          confirmLabel: t("health.sync_now"),
          cancelLabel: t("health.disconnect"),
        }));
        if (disconnect) {
          disconnectHealth();
          setHealthOn(false);
          toast.success(t("health.disconnected_toast"));
        } else {
          await syncHealthSteps();
          toast.success(t("toast.saved"));
        }
      }
    } catch {
      toast.error(t("toast.failed"));
    } finally {
      setHealthBusy(false);
    }
  }

  return (
    <div className="space-y-6 pt-4 animate-rise">
      <PageHeader back="history" title={t("settings.preferences")} />

      <SettingsGroup>
        {/* Appearance — light/dark (owner request). Device-level like text
            size; the Get Started flow stays light regardless (lib/theme.ts). */}
        <div className="px-4 py-3.5 space-y-2">
          <div className="flex items-center gap-3">
            <IconChip icon={theme === "light" ? Sun : Moon} tint="#8b5cf6" />
            <div className="flex-1 min-w-0">
              <div className="text-base">{t("settings.appearance")}</div>
              <div className="text-sm text-fg-muted">{t("settings.appearance_hint")}</div>
            </div>
          </div>
          <div className="flex gap-1.5" role="group" aria-label={t("settings.appearance")}>
            {(["dark", "light"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => { hapticSelect(); setTheme(mode); setThemeState(mode); }}
                aria-pressed={theme === mode}
                className={`flex-1 min-h-[44px] rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${
                  theme === mode ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary active:bg-surface-strong"
                }`}
              >
                {mode === "dark" ? <Moon size={15} aria-hidden="true" /> : <Sun size={15} aria-hidden="true" />}
                {t(`theme.${mode}`)}
              </button>
            ))}
          </div>
        </div>

        <SettingsButton
          onClick={() => setShowLang(true)}
          icon={Globe}
          tint={TINT.language}
          label={t("more.language")}
          value={LOCALE_LABEL[locale]}
        />

        <div className="px-4 py-3.5 space-y-2">
          <div className="flex items-center gap-3">
            <IconChip icon={Type} tint={TINT.language} />
            <div className="flex-1 min-w-0">
              <div className="text-base">{t("settings.text_size")}</div>
              <div className="text-sm text-fg-muted">{t("settings.text_size_hint")}</div>
            </div>
          </div>
          <div className="flex gap-1.5" role="group" aria-label={t("settings.text_size")}>
            {TEXT_ZOOMS.map((z) => (
              <button
                key={z}
                type="button"
                onClick={() => { hapticSelect(); setTextZoom(z); setTextZoomState(z); }}
                aria-pressed={textZoom === z}
                className={`flex-1 min-h-[44px] rounded-xl text-sm font-medium transition-colors ${
                  textZoom === z ? "bg-brand text-on-brand" : "bg-surface text-fg-secondary active:bg-surface-strong"
                }`}
              >
                {t(`textsize.${z === 0.9 ? "small" : z === 1.0 ? "default" : z === 1.15 ? "large" : "xl"}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 px-4 py-3.5">
          <IconChip icon={Vibrate} tint={TINT.reminders} />
          <div className="flex-1 min-w-0">
            <div className="text-base">{t("settings.haptics")}</div>
            <div className="text-sm text-fg-muted">{t("settings.haptics_hint")}</div>
          </div>
          <Toggle
            checked={hapticsOn}
            onChange={(on) => {
              setHapticsDisabled(!on);
              setHapticsOn(on);
              if (on) hapticSelect();
            }}
            label={t("settings.haptics")}
          />
        </div>

        {healthAvailable && (
          <div className="flex items-center gap-3 px-4 py-3.5">
            <IconChip icon={HeartPulse} tint={TINT.health} />
            <div className="flex-1 min-w-0">
              <div className="text-base">{t("health.title")}</div>
              <div className="text-sm text-fg-muted">
                {healthOn && healthToday && (healthToday.steps != null || healthToday.active_kcal != null)
                  ? [
                      healthToday.steps != null ? t("health.today_steps", { n: healthToday.steps.toLocaleString() }) : null,
                      healthToday.active_kcal != null ? t("health.today_kcal", { n: healthToday.active_kcal.toLocaleString() }) : null,
                    ].filter(Boolean).join(" · ")
                  : t("health.hint")}
              </div>
            </div>
            {healthOn ? (
              <button
                type="button"
                onClick={tapHealth}
                disabled={healthBusy}
                className="flex items-center gap-1 text-sm px-2.5 py-1 bg-success/20 text-success rounded-full disabled:opacity-50"
              >
                <Check size={14} aria-hidden="true" />
                {t("health.connected")}
              </button>
            ) : (
              <button
                type="button"
                onClick={tapHealth}
                disabled={healthBusy}
                className="text-sm px-3 py-1.5 bg-brand text-on-brand rounded-full font-medium press disabled:opacity-50"
              >
                {t("health.connect")}
              </button>
            )}
          </div>
        )}

        {/* Six kinds, their times and quiet hours are a screen of their own —
            the same reason this page exists rather than living in More. */}
        <SettingsButton
          onClick={() => navigate("/more/reminders")}
          icon={Bell}
          tint={TINT.reminders}
          label={t("reminders.title")}
          value={remindersAvailable ? enabledSummary : t("reminders.unavailable_short")}
        />
      </SettingsGroup>

      <LanguageSheet open={showLang} onClose={() => setShowLang(false)} />
    </div>
  );
}
